"use client";

import { useState, useRef } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Variant, CartItem } from "@/types/models/order";
import { Package } from "lucide-react";
import { formatCurrency } from "@/utils/currency";
import { getVariantImageUrl } from "@/utils/helper";

interface ItemsPopoverProps {
  variants: Variant[];
  items: CartItem[];
  trigger: React.ReactNode;
}

interface OrderItemData {
  variant: Variant;
  quantity: number;
}

export function ItemsPopover({ variants, items, trigger }: ItemsPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100); // Small delay to allow moving to popover
  };

  if (!variants || variants.length === 0) {
    return <>{trigger}</>;
  }

  // Combine variants with their quantities from items
  const orderItems: OrderItemData[] = variants.map((variant) => {
    const item = items?.find((item) => item.variant_id === variant.id);
    return {
      variant,
      quantity: item?.quantity || 1,
    };
  });

  const totalValue = orderItems.reduce(
    (sum, item) => sum + item.variant.price * item.quantity,
    0,
  );
  const totalQuantity = orderItems.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          className="cursor-pointer"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {trigger}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 bg-card border border-border/40 shadow-lg"
        align="start"
        side="right"
        sideOffset={-20}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border/40">
            <Package className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">
              Order Items ({orderItems.length})
            </h3>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {orderItems.map((orderItem, index) => {
              const { variant, quantity } = orderItem;
              const lineTotal = variant.price * quantity;
              const imageUrl = getVariantImageUrl(variant.assets, 80, 80);

              return (
                <div
                  key={variant.id || index}
                  className="flex items-start gap-3 p-2 rounded-md hover:bg-muted/30 transition-colors"
                >
                  {/* Item Image */}
                  <div className="w-10 h-10 bg-muted rounded-md shrink-0 overflow-hidden flex items-center justify-center">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={variant.title || "Product"}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <Package className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs font-medium text-foreground truncate">
                      {variant.title || "Unknown Item"}
                    </h4>

                    {/* SKU and Options */}
                    <div className="flex flex-col gap-1 mt-1">
                      {variant.sku && (
                        <span className="text-xs text-gray-600 font-mono">
                          SKU: {variant.sku}
                        </span>
                      )}

                      {/* Variant Options */}
                      <div className="flex flex-wrap gap-1">
                        {variant.option1 && (
                          <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full text-gray-600 dark:text-gray-400">
                            {variant.option1}
                          </span>
                        )}
                        {variant.option2 && (
                          <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full text-gray-600 dark:text-gray-400">
                            {variant.option2}
                          </span>
                        )}
                        {variant.option3 && (
                          <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full text-gray-600 dark:text-gray-400">
                            {variant.option3}
                          </span>
                        )}
                      </div>

                      {/* Quantity */}
                      <div className="text-xs text-gray-600">
                        Qty: {quantity}
                      </div>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-right shrink-0">
                    <div className="text-xs font-semibold text-foreground">
                      {formatCurrency(lineTotal)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatCurrency(variant.price)} each
                    </div>
                    {variant.listing_price !== variant.price && (
                      <div className="text-xs text-gray-500 line-through">
                        {formatCurrency(variant.listing_price)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total Summary */}
          <div className="mt-3 pt-3 border-t border-border/40">
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Total Quantity:</span>
                <span className="text-xs font-medium text-foreground">
                  {totalQuantity} unit{totalQuantity !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-foreground">
                  Total Value:
                </span>
                <span className="text-xs font-semibold text-foreground">
                  {formatCurrency(totalValue)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
